import React, { useEffect, useState } from "react";
import { LoaderIcon } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { ADDRESS_PLACEHOLDER } from "../../utils/siteAddress";
import { geocodeDomicileAddresses } from "../../utils/geocodeAddress";

/**
 * Confirm one address per unique domicile (Create Account or Replace Fleet).
 * Conflicts (multiple CSV addresses for one domicile) show a picker.
 * On confirm, Geocodio verifies each address and converts it to latitude/longitude.
 */
const AddressConfirmationDialog = ({
  open,
  groups = [],
  confirming = false,
  confirmLabel = "Confirm & create account",
  confirmingLabel = "Creating account…",
  description = "Each unique domicile must use one address",
  onCancel,
  onConfirm,
}) => {
  const [selections, setSelections] = useState({});
  const [errors, setErrors] = useState({});
  const [isGeocoding, setIsGeocoding] = useState(false);
  const [geocodeStatus, setGeocodeStatus] = useState("");

  const busy = confirming || isGeocoding;

  useEffect(() => {
    if (!open) return;
    const next = {};
    for (const group of groups) {
      next[group.domicile] = group.selectedAddress || "";
    }
    setSelections(next);
    setErrors({});
    setIsGeocoding(false);
    setGeocodeStatus("");
  }, [open, groups]);

  const setAddress = (domicile, address) => {
    setSelections((prev) => ({ ...prev, [domicile]: address }));
    setGeocodeStatus("");
    setErrors((prev) => {
      if (!prev[domicile]) return prev;
      const next = { ...prev };
      delete next[domicile];
      return next;
    });
  };

  const focusFirstInvalid = (errorMap) => {
    const firstDomicile = groups.find((group) => errorMap[group.domicile])?.domicile;
    if (!firstDomicile) return;
    requestAnimationFrame(() => {
      document.getElementById(`domicile-address-input-${firstDomicile}`)?.focus();
    });
  };

  const handleConfirm = async () => {
    const domicileAddressMap = {};

    for (const group of groups) {
      domicileAddressMap[group.domicile] = String(selections[group.domicile] ?? "").trim();
    }

    setErrors({});
    setIsGeocoding(true);
    setGeocodeStatus("");

    try {
      const { results, errors: geocodeErrors } = await geocodeDomicileAddresses(domicileAddressMap);

      if (Object.keys(geocodeErrors).length > 0) {
        setErrors(geocodeErrors);
        setGeocodeStatus("Fix the addresses that could not be found, then try again.");
        focusFirstInvalid(geocodeErrors);
        return;
      }

      setGeocodeStatus("");
      await onConfirm?.(results);
    } catch (error) {
      setGeocodeStatus(error?.message || "Location lookup failed");
    } finally {
      setIsGeocoding(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen && !busy) onCancel?.();
      }}>
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Confirm site addresses</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-5 py-2">
          {groups.length === 0 ? (
            <p className="text-sm text-muted-foreground">No domiciles found in the Vehicle CSV.</p>
          ) : (
            groups.map((group) => {
              const value = selections[group.domicile] ?? "";
              const error = errors[group.domicile];

              return (
                <div
                  key={group.domicile}
                  className={`rounded-md border p-4 ${error ? "border-red-400" : "border-border"}`}>
                  <Label
                    htmlFor={`domicile-address-input-${group.domicile}`}
                    className="text-sm font-medium text-[#40423f]">
                    {group.domicile}
                  </Label>

                  {group.hasConflict && (
                    <div className="mt-3 space-y-2">
                      <p className="text-xs text-amber-700">
                        Multiple addresses found — pick one, or type a custom address below.
                      </p>
                      {group.addresses.map((address) => (
                        <label
                          key={address}
                          className="flex cursor-pointer items-start gap-2 text-sm">
                          <input
                            type="radio"
                            className="mt-1"
                            name={`domicile-address-${group.domicile}`}
                            checked={value === address}
                            onChange={() => setAddress(group.domicile, address)}
                            disabled={busy}
                          />
                          <span>{address}</span>
                        </label>
                      ))}
                    </div>
                  )}

                  <Input
                    id={`domicile-address-input-${group.domicile}`}
                    className={`mt-3 ${error ? "border-red-500 focus-visible:ring-red-500" : ""}`}
                    value={value}
                    placeholder={ADDRESS_PLACEHOLDER}
                    onChange={(e) => setAddress(group.domicile, e.target.value)}
                    disabled={busy}
                    aria-invalid={Boolean(error)}
                    aria-describedby={
                      error ? `domicile-address-error-${group.domicile}` : undefined
                    }
                  />
                  {error && (
                    <p
                      id={`domicile-address-error-${group.domicile}`}
                      className="mt-1 text-xs text-red-500">
                      {error}
                    </p>
                  )}
                </div>
              );
            })
          )}
        </div>

        {geocodeStatus && (
          <p
            className={`text-sm ${Object.keys(errors).length > 0 ? "text-red-500" : "text-muted-foreground"}`}
            role="status"
            aria-live="polite">
            {geocodeStatus}
          </p>
        )}

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={busy}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleConfirm}
            disabled={busy || groups.length === 0}
            aria-busy={isGeocoding}
            aria-label={isGeocoding ? "Looking up locations" : undefined}>
            {isGeocoding ? (
              <LoaderIcon
                className="h-5 w-5 animate-spin"
                aria-hidden
              />
            ) : confirming ? (
              confirmingLabel
            ) : (
              confirmLabel
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddressConfirmationDialog;
